/**
 * The public national overview: `/data/skoler.json`, bundled with each release from the national
 * demo's `national.json` by `scripts/public_data.py` (same shape), not served by the API. Only the fields the public pages need are typed; extras are ignored and
 * optional fields may be missing.
 */

export type SchoolStatus = "red" | "yellow" | "green" | "grey";

/** Display order for schools within a kommune: worst first. */
export const STATUS_ORDER: readonly SchoolStatus[] = ["red", "yellow", "grey", "green"];

export type StatusCounts = Record<SchoolStatus, number>;

/** What surrounds a finding's stretch: enough homes along it, or few. Absent in older data. */
export type Omgivelse = "boliger" | "faa_boliger";

export interface NationalFinding {
  title: string;
  location: string;
  severity: string;
  exposure: number;
  tiltak: string | null;
  omgivelse: Omgivelse | null;
  adresser_langs: number | null;
}

/** Distinct addresses by what their route to the school passes. Absent in older data. */
export interface NationalExposure {
  addresses: number;
  with_high: number;
  with_high_faa_boliger: number;
  with_medium_or_high: number;
}

export interface NationalSchool {
  school_id: string;
  name: string;
  kommune: string;
  kommune_name: string;
  status: SchoolStatus;
  reason: string;
  trinn: string | null;
  addresses: number;
  exposure: NationalExposure | null;
  top_findings: NationalFinding[];
}

export interface NationalRun {
  kommune: string;
  name: string;
  snapshot_at: string | null;
  warnings: string[];
  attribution: string | null;
}

export interface NationalData {
  generated_at: string | null;
  totals: { kommuner: number; schools: number } | null;
  rule: { description: string } | null;
  runs: NationalRun[];
  schools: NationalSchool[];
}

export interface KommuneSummary {
  nr: string;
  name: string;
  schools: number;
  counts: StatusCounts;
}

const NATIONAL_URL = "/data/skoler.json";

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function optStr(v: unknown): string | null {
  return typeof v === "string" && v !== "" ? v : null;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function optNum(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function toStatus(v: unknown): SchoolStatus {
  return v === "red" || v === "yellow" || v === "green" || v === "grey" ? v : "grey";
}

function toFinding(raw: unknown): NationalFinding | null {
  if (!isRecord(raw) || typeof raw.title !== "string") return null;
  return {
    title: raw.title,
    location: str(raw.location),
    severity: str(raw.severity),
    exposure: num(raw.exposure),
    tiltak: optStr(raw.tiltak),
    omgivelse:
      raw.omgivelse === "boliger" || raw.omgivelse === "faa_boliger" ? raw.omgivelse : null,
    adresser_langs: optNum(raw.adresser_langs),
  };
}

function toExposure(raw: unknown): NationalExposure | null {
  if (!isRecord(raw)) return null;
  const addresses = optNum(raw.addresses);
  const withHigh = optNum(raw.with_high);
  const withHighFew = optNum(raw.with_high_faa_boliger);
  const withMediumOrHigh = optNum(raw.with_medium_or_high);
  if (
    addresses === null ||
    withHigh === null ||
    withHighFew === null ||
    withMediumOrHigh === null
  ) {
    return null;
  }
  return {
    addresses,
    with_high: withHigh,
    with_high_faa_boliger: withHighFew,
    with_medium_or_high: withMediumOrHigh,
  };
}

function toSchool(raw: unknown): NationalSchool | null {
  if (!isRecord(raw) || typeof raw.kommune !== "string" || typeof raw.name !== "string") {
    return null;
  }
  return {
    school_id: str(raw.school_id, `${raw.kommune}-${raw.name}`),
    name: raw.name,
    kommune: raw.kommune,
    kommune_name: str(raw.kommune_name),
    status: toStatus(raw.status),
    reason: str(raw.reason),
    trinn: optStr(raw.trinn),
    addresses: num(raw.addresses),
    exposure: toExposure(raw.exposure),
    top_findings: Array.isArray(raw.top_findings)
      ? raw.top_findings.map(toFinding).filter((f): f is NationalFinding => f !== null)
      : [],
  };
}

function toRun(raw: unknown): NationalRun | null {
  if (!isRecord(raw) || typeof raw.kommune !== "string" || typeof raw.name !== "string") {
    return null;
  }
  const coverage = isRecord(raw.coverage) ? raw.coverage : {};
  return {
    kommune: raw.kommune,
    name: raw.name,
    snapshot_at: optStr(raw.snapshot_at),
    warnings: Array.isArray(coverage.warnings)
      ? coverage.warnings.filter((w): w is string => typeof w === "string")
      : [],
    attribution: optStr(raw.attribution),
  };
}

/** Check the top-level shape of the file and read the fields the public pages use. */
export function parseNational(raw: unknown): NationalData {
  if (!isRecord(raw)) throw new Error("national.json: expected an object");
  if (!Array.isArray(raw.runs)) throw new Error("national.json: missing 'runs' list");
  if (!Array.isArray(raw.schools)) throw new Error("national.json: missing 'schools' list");
  const totals = isRecord(raw.totals) ? raw.totals : null;
  const rule = isRecord(raw.rule) ? raw.rule : null;
  return {
    generated_at: optStr(raw.generated_at),
    totals: totals ? { kommuner: num(totals.kommuner), schools: num(totals.schools) } : null,
    rule: rule ? { description: str(rule.description) } : null,
    runs: raw.runs.map(toRun).filter((r): r is NationalRun => r !== null),
    schools: raw.schools.map(toSchool).filter((s): s is NationalSchool => s !== null),
  };
}

let cached: Promise<NationalData> | null = null;

/** Fetch the national file once per page load. A failure clears the cache so a retry refetches. */
export function loadNational(): Promise<NationalData> {
  if (!cached) {
    const request = (async () => {
      const response = await fetch(NATIONAL_URL, { headers: { Accept: "application/json" } });
      if (!response.ok) throw new Error(`national.json: HTTP ${response.status}`);
      return parseNational(await response.json());
    })();
    cached = request;
    request.catch(() => {
      if (cached === request) cached = null;
    });
  }
  return cached;
}

/** Forget the cached file (tests). */
export function clearNationalCache(): void {
  cached = null;
}

const emptyCounts = (): StatusCounts => ({ red: 0, yellow: 0, green: 0, grey: 0 });

/** One entry per run, sorted by name. Counts are taken from the schools actually listed. */
export function kommuneSummaries(data: NationalData): KommuneSummary[] {
  const byKommune = new Map<string, KommuneSummary>();
  for (const run of data.runs) {
    byKommune.set(run.kommune, {
      nr: run.kommune,
      name: run.name,
      schools: 0,
      counts: emptyCounts(),
    });
  }
  for (const school of data.schools) {
    const summary = byKommune.get(school.kommune);
    if (!summary) continue;
    summary.schools += 1;
    summary.counts[school.status] += 1;
  }
  return [...byKommune.values()].sort(
    (a, b) => a.name.localeCompare(b.name, "nb") || a.nr.localeCompare(b.nr),
  );
}

/** The kommune's schools: red, yellow, grey, green, then by name. */
export function schoolsForKommune(data: NationalData, nr: string): NationalSchool[] {
  return data.schools
    .filter((s) => s.kommune === nr)
    .sort(
      (a, b) =>
        STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
        a.name.localeCompare(b.name, "nb"),
    );
}

/**
 * Addresses with at least one serious finding on the way to school, summed over the schools that
 * report it. `null` when no school does (older data), so the page can leave the line out.
 */
export function kommuneExposure(
  schools: readonly NationalSchool[],
): { addresses: number; with_high: number } | null {
  let found = false;
  let addresses = 0;
  let withHigh = 0;
  for (const school of schools) {
    if (!school.exposure) continue;
    found = true;
    addresses += school.exposure.addresses;
    withHigh += school.exposure.with_high;
  }
  return found ? { addresses, with_high: withHigh } : null;
}

/** Kommuner whose number starts with the query or whose name contains it (prefix matches first). */
export function searchKommuner(
  summaries: readonly KommuneSummary[],
  query: string,
  limit = 8,
): KommuneSummary[] {
  const q = query.trim().toLocaleLowerCase("nb");
  if (!q) return [];
  const starts: KommuneSummary[] = [];
  const contains: KommuneSummary[] = [];
  for (const k of summaries) {
    const name = k.name.toLocaleLowerCase("nb");
    if (name.startsWith(q) || k.nr.startsWith(q)) starts.push(k);
    else if (name.includes(q)) contains.push(k);
  }
  return [...starts, ...contains].slice(0, limit);
}
